import * as adminActions from "../../store/actions/temp/admin/adminActions";
import BigLoader from "../common/BigLoader";
import PasswordField from "./PasswordField";
import UserDetail from "./UserDetail";
import { validatePassword } from "./UserManagement.support";
import {
  PersonAddRounded as AddUserIcon,
  CloseRounded as CloseIcon,
  CheckCircleOutlineRounded as SuccessIcon,
  ErrorOutlineRounded as FaiiledIcon,
  LockResetRounded as PasswordResetIcon,
  FileDownloadRounded as DownloadIcon,
} from "@mui/icons-material";
import {
  Box,
  CircularProgress,
  Grid,
  Paper,
  Switch,
  Tooltip,
  Typography,
  Stack,
  Dialog,
} from "@mui/material";
import MDEditor from "@uiw/react-md-editor/nohighlight";
import * as dates from "common/dates";
import * as strings from "common/strings";
import Component from "components/Component";
import AdminDashboard from "components/admin/AdminDashboard";
import Button from "components/common/Button";
import IconButton from "components/common/IconButton";
import InlineSearch from "components/common/InlineSearch";
import LabelledCheckbox from "components/common/LabelledCheckbox";
import TextField from "components/common/TextField";
import DashboardContent from "components/layout/DashboardContent";
import * as config from "config";
import { Formik } from "formik";
import { useSnackbar } from "notistack";
import React from "react";
import { useState, useEffect } from "react";
import { connect } from "react-redux";
import { useParams } from "react-router-dom";
import {
  useCreateUserMutation,
  useConfigureUserMutation,
  useLazyListUsersQuery,
  useResetUserPasswordMutation,
  useGetAllowedUserPermissionsQuery,
  useDownloadCompanyUserAclPdfMutation,
} from "services/adminService";
import * as adminSelectors from "store/selectors/temp/admin/adminSelectors";
import * as yup from "yup";

const readableName = (permission) =>
  strings.toSentenceCase(
    permission.replace("web:", "").replace("forms:", "Forms_")
  );

const userSort = (a, b) => {
  if (a.enabled && !b.enabled) {
    return -1;
  }
  if (!a.enabled && b.enabled) {
    return 1;
  }
  return a.email.localeCompare(b.email);
};

const User = ({
  permissionsList,
  user,
  onChange,
  disabled,
  resettingPassword,
  onReset,
}) => {
  const [expireAll, setExpireAll] = useState(true);
  const [expireList, setExpireList] = useState([]);
  const [expireDate, setExpireDate] = useState(null);
  const setExpirePerm = (name, active) => {
    setExpireList((el) => {
      if (active) {
        if (!el.includes(name)) return [...el, name];
        return el;
      } else {
        if (el.includes(name)) return el.filter((e) => e !== name);
        return el;
      }
    });
  };
  const invalidDate = !/^[0-9]{4}(-[0-9]{2}){2}([ T][0-9]{2}:[0-9]{2})?$/.test(
    expireDate
  );
  const timeouts = Object.entries(
    user.timeouts ?? {}
  ).sort(([at, _], [bt, __]) => (at > bt ? 1 : at < bt ? -1 : 0));
  const perms = user.perms ?? [];
  return (
    <Paper variant={"outlined"} sx={{ padding: "0.5rem 1rem 0.75rem 1rem" }}>
      <Grid container alignItems={"center"} spacing={0}>
        <Grid item xs={9}>
          <UserDetail email={user.email} id={user.username} />
        </Grid>
        <Grid item xs={3}>
          <Stack
            direction={"row"}
            justifyContent={"flex-end"}
            alignItems={"center"}
          >
            <Switch
              checked={user.enabled}
              onChange={(e) => {
                onChange({
                  user,
                  enabled: e.target.checked,
                });
              }}
              disabled={disabled}
            />
            {resettingPassword === user.email ? (
              <CircularProgress size={24} style={{ margin: 8 }} />
            ) : (
              <IconButton
                icon={
                  user?.hasPasswordReset === "failure"
                    ? () => (
                        <Tooltip
                          title={
                            "An error has occured when resetting the password, please contact support at: support@cactus.bm"
                          }
                        >
                          <FaiiledIcon />
                        </Tooltip>
                      )
                    : user?.hasPasswordReset === "success"
                    ? SuccessIcon
                    : PasswordResetIcon
                }
                onClick={onReset}
                isDisabled={
                  disabled ||
                  user?.hasPasswordReset === "success" ||
                  resettingPassword != null
                }
              />
            )}
          </Stack>
        </Grid>
        <Grid item xs={12}>
          <Stack
            direction={"row"}
            alignItems={"center"}
            gap={1}
            flexWrap={"wrap"}
          >
            {permissionsList.map((perm, index) => (
              <LabelledCheckbox
                key={index}
                label={readableName(perm)}
                checked={perms.includes(perm)}
                onClick={(e) => {
                  onChange({
                    user,
                    perms: {
                      [perm]: e.target.checked,
                    },
                  });
                }}
                disabled={disabled}
              />
            ))}
            {perms
              .filter((perm) => !permissionsList.includes(perm))
              .map((perm, index) => (
                <LabelledCheckbox
                  key={index}
                  label={perm}
                  checked={perms.includes(perm)}
                  disabled={true}
                />
              ))}
          </Stack>
        </Grid>
        {timeouts?.length ? (
          timeouts.map(([time, pList], index) => (
            <Grid item xs={12} key={index}>
              <Stack direction={"row"} alignItems={"center"}>
                <div>
                  {"Expire "}
                  <span style={{ fontWeight: 600 }}>
                    {(pList ?? ["All"])
                      .map((perm) => readableName(perm).toLowerCase())
                      .join(", ")}
                  </span>
                  {" permissions "}
                  {dates.readableDate(time, { includeTime: true })}
                </div>
                <Button
                  color={"primary"}
                  checked={true}
                  onClick={(e) => {
                    onChange({
                      user,
                      timeoutDelete: [time],
                    });
                  }}
                  disabled={disabled}
                  sx={{ margin: "10px" }}
                >
                  {"Delete"}
                </Button>
              </Stack>
            </Grid>
          ))
        ) : (
          <Grid item xs={12} marginTop={1}>
            <Stack
              direction={"row"}
              alignItems={"center"}
              gap={1}
              flexWrap={"wrap"}
            >
              <Grid item md={4}>
                <TextField
                  label={"Expiry Date/Time"}
                  onChange={(e) => setExpireDate(e.target.value)}
                  value={expireDate}
                  disabled={disabled}
                  placeholder={"ISO date with optional hour:minute"}
                />
              </Grid>
              <Grid item md={1}>
                <Button
                  color={"primary"}
                  checked={true}
                  onClick={(e) => {
                    onChange({
                      user,
                      timeoutAdd: {
                        [expireDate]: expireAll ? [] : expireList,
                      },
                    });
                  }}
                  disabled={disabled || invalidDate}
                  sx={{ margin: "0 0.5rem" }}
                >
                  {"Add expiry"}
                </Button>
              </Grid>
              <LabelledCheckbox
                label={"All"}
                checked={expireAll}
                onChange={(e) => setExpireAll(e.target.checked)}
                disabled={disabled}
              />
              {permissionsList.map((perm, index) => (
                <LabelledCheckbox
                  key={index}
                  label={readableName(perm)}
                  checked={expireList.includes(perm)}
                  onChange={(e) => setExpirePerm(perm, e.target.checked)}
                  disabled={disabled || expireAll}
                />
              ))}
            </Stack>
          </Grid>
        )}
      </Grid>
    </Paper>
  );
};

const AsOfDate = ({ timestamp }) => {
  const asOfDate = dates.asOfDate(timestamp, { includeDayOfWeek: true });
  return asOfDate ? <Typography>{`As of ${asOfDate}`}</Typography> : <></>;
};

const NewUser = ({ saveUserLogMessage, userLogMessage, hasUserLogMessage }) => {
  const {
    data: permissionsList,
    status: permissionsListStatus,
  } = useGetAllowedUserPermissionsQuery();
  const [formikPerms, setFormikPerms] = useState([]);
  const [formikPermToPerm, setFormikPermToPerm] = useState({});
  const [formikInitialValues, setFormikInitialValues] = useState({});
  useEffect(() => {
    if (permissionsListStatus === "fulfilled") {
      const formikInitial = {
        email: "",
        enabled: true,
        sendEmail: true,
        setPassword: false,
        password: "",
        timeoutRequired: false,
        timeoutDate: "",
        expireAll: true,
      };
      const defPerms = permissionsList?.defaultPermissions || [];
      const entries = permissionsList?.permissions.map((p) => [
        "perm_" + p.replaceAll(/[:,-]/g, "_"),
        p,
      ]);
      entries.forEach(([formik, raw]) => {
        const set = defPerms.includes(raw);
        formikInitial[formik] = set;
        formikInitial["expire_" + formik] = set;
      });
      setFormikInitialValues(formikInitial);
      setFormikPerms(entries.map(([formik, _]) => formik));
      setFormikPermToPerm(Object.fromEntries(entries));
    }
  }, [permissionsList, permissionsListStatus, setFormikInitialValues]);

  const { companyId } = useParams();

  const { enqueueSnackbar: queueMessage } = useSnackbar();
  const addMessage = (variant, message) => {
    queueMessage(message, {
      variant,
      autoHideDuration: 5_000,
      preventDuplicate: true,
    });
  };
  const [resettingPassword, setResettingPassword] = useState(null);
  const [resetPasswords, setResetPasswords] = useState({});
  const [resetUserPassword] = useResetUserPasswordMutation();
  const [newUserDialogOpen, setNewUserDialogOpen] = useState(false);
  const [newUser, setNewUser] = useState(null);
  const [searchText, setSearchText] = useState("");
  const [responseData, setResponseData] = useState(null);
  const [createUser, { isLoading, isError }] = useCreateUserMutation();
  const [
    configureUser,
    { isLoading: configureLoading },
  ] = useConfigureUserMutation();
  const [
    triggerListUsers,
    { data: users, isFetching: usersLoading, startedTimeStamp: usersTimestamp },
  ] = useLazyListUsersQuery();
  const [
    downloadUserAclPdf,
    { isLoading: aclPdfLoading },
  ] = useDownloadCompanyUserAclPdfMutation();
  const [aclPdfActiveOnly, setAclPdfActiveOnly] = useState(false);
  useEffect(() => {
    if (companyId) {
      triggerListUsers({ companyId });
    }
  }, [companyId, triggerListUsers]);
  const onChange = ({ user, enabled, perms, timeoutDelete, timeoutAdd }) => {
    const config = {
      username: user.username,
      enabled,
      perms,
      timeoutDelete,
      timeoutAdd,
      userLogMessage,
    };
    configureUser(config).then((response) => {
      if (!response.data) {
        addMessage("error", `Failed to update ${user.email}`);
      } else if (response.data.changes) {
        addMessage("success", `${user.email} updated successfully`);
      } else {
        addMessage("warning", `No changes made to ${user.email}`);
      }
      triggerListUsers({ companyId });
    });
  };

  const usersToDisplay =
    searchText === ""
      ? users ?? []
      : (users ?? []).filter((user) =>
          user.email.toLowerCase().includes(searchText)
        );

  const showNoMatches = searchText !== "" && usersToDisplay.length === 0;

  const permList = permissionsList?.permissions || [];

  if (permissionsListStatus === "rejected") {
    return (
      <Box sx={{ margin: 2, textAlign: "center" }}>
        <Typography variant={"subTitle1"}>
          {"System failure: failed to read allowed permissions"}
        </Typography>
      </Box>
    );
  }

  if (permissionsListStatus !== "fulfilled" || !formikPerms.length)
    return <BigLoader />;
  return (
    <AdminDashboard>
      <DashboardContent isFullWidth={false}>
        <Grid item xs={12}>
          <Component
            title={"Users"}
            loading={!users && usersLoading}
            options={
              <Stack direction={"row"} gap={1} alignItems={"center"}>
                <InlineSearch
                  placeholder={"Quick Filter"}
                  onChange={(searchText) =>
                    setSearchText(searchText.toLowerCase())
                  }
                />
                <Button
                  startIcon={<AddUserIcon />}
                  onClick={() => setNewUserDialogOpen(true)}
                  size={"small"}
                >
                  {"Add User"}
                </Button>
                {companyId && (
                  <LabelledCheckbox
                    label={"Active users only"}
                    checked={aclPdfActiveOnly}
                    onChange={(e) => setAclPdfActiveOnly(e.target.checked)}
                  />
                )}
                {companyId && (
                  <Button
                    startIcon={<DownloadIcon />}
                    size={"small"}
                    disabled={aclPdfLoading}
                    onClick={() =>
                      downloadUserAclPdf({
                        companyId,
                        activeOnly: aclPdfActiveOnly,
                      }).then((response) => {
                        if (response.error) {
                          addMessage(
                            "error",
                            "Failed to download user/ACL PDF"
                          );
                        }
                      })
                    }
                  >
                    {"Download PDF"}
                  </Button>
                )}
              </Stack>
            }
          >
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <AsOfDate timestamp={usersTimestamp} />
              </Grid>
              <Grid
                item
                xs={12}
                sx={{
                  position: "sticky",
                  top: "-1rem",
                  background: "white",
                  zIndex: 100,
                }}
              >
                {users && (
                  <TextField
                    variant={"filled"}
                    label={"Reason for updating users"}
                    fullWidth
                    onChange={(e) => saveUserLogMessage(e.target.value)}
                    value={userLogMessage}
                  />
                )}
              </Grid>
              {[...usersToDisplay].sort(userSort).map((user) => (
                <Grid key={user.username} item xs={12}>
                  <User
                    user={{
                      ...user,
                      hasPasswordReset: resetPasswords[user.email],
                    }}
                    permissionsList={permList}
                    resettingPassword={resettingPassword}
                    disabled={
                      configureLoading || usersLoading || !hasUserLogMessage
                    }
                    onChange={onChange}
                    onReset={() => {
                      setResettingPassword(user.email);
                      resetUserPassword({
                        username: user.email,
                        userLogMessage,
                        companyId,
                      })
                        .then((response) => {
                          if (response?.data) {
                            setResetPasswords({
                              ...resetPasswords,
                              [user.email]: "success",
                            });
                          } else {
                            setResetPasswords({
                              ...resetPasswords,
                              [user.email]: "failure",
                            });
                          }
                          setResettingPassword(null);
                        })
                        .catch(() => {
                          setResetPasswords({
                            ...resetPasswords,
                            [user.email]: "failure",
                          });
                          setResettingPassword(null);
                        });
                    }}
                  />
                </Grid>
              ))}
            </Grid>
            {showNoMatches && (
              <Stack
                alignItems={"center"}
                justifyContent={"center"}
                sx={{ padding: "4rem" }}
              >
                <Typography color={"grey"}>
                  {`No matches found for '${searchText}'`}
                </Typography>
              </Stack>
            )}
          </Component>
        </Grid>
        {companyId && (
          <Dialog open={newUserDialogOpen} fullWidth maxWidth={"md"}>
            <Formik
              initialValues={formikInitialValues}
              validationSchema={yup.object({
                email: yup
                  .string("The email address of the new user.")
                  .email(({ value }) => `${value} is not a valid email`)
                  .required("Email is required"),
                timeoutRequired: yup.boolean(),
                timeoutDate: yup.string().when("timeoutRequired", {
                  is: true,
                  then: yup
                    .string()
                    .matches(
                      /^[0-9]{4}(-[0-9]{2}){2}([T ]([0-9]{2}:[0-9]{2}))?$/,
                      "Must be iso style: year-month-day or year-month-day hour:minute"
                    )
                    .required("To expire permissions a date must be provided"),
                }),
                setPassword: yup.boolean(),
                // PasswordField shows these errors itself; this only stops
                // the form being submitted with one
                password: yup.string().when("setPassword", {
                  is: true,
                  then: yup.string().test({
                    name: "password",
                    test(value) {
                      const error = validatePassword(value);
                      return error
                        ? this.createError({ message: error })
                        : true;
                    },
                  }),
                  otherwise: yup.string().notRequired(),
                }),
              })}
              onSubmit={(values) => {
                const createUserParams = {
                  ...values,
                  password: values.setPassword ? values.password : undefined,
                  companyId,
                  userLogMessage,
                };
                const entries = Object.entries(values);
                const perms = entries
                  .filter(([name, val]) => formikPerms.includes(name))
                  .map(([k, v]) => [formikPermToPerm[k], v]);
                createUserParams["perms"] = Object.fromEntries(perms);
                if (values.timeoutRequired) {
                  if (values.expireAll) {
                    createUserParams["timeout"] = {
                      [values.timeoutDate]: [],
                    };
                  } else {
                    const expirePermisions = entries
                      .filter(
                        ([permission, required]) =>
                          permission.startsWith("expire_") &&
                          formikPerms.includes(permission.substring(7)) &&
                          required
                      )
                      .map(([name, v]) => formikPermToPerm[name.substring(7)]);
                    createUserParams["timeout"] = {
                      [values.timeoutDate]: expirePermisions,
                    };
                  }
                }
                createUser(createUserParams).then((response) => {
                  setResponseData(
                    `\`\`\`\n${JSON.stringify(
                      response?.data ?? response,
                      null,
                      2
                    )}\n\`\`\``
                  );
                  setNewUser(response?.data);
                  triggerListUsers({ companyId });
                  if (isError)
                    addMessage("error", `User ${values.email} creation failed`);
                  else
                    addMessage(
                      "success",
                      `User ${values.email} created successfully`
                    );
                });
              }}
            >
              {(formik) => (
                <Component
                  title={`New User (${companyId
                    .substring(0, companyId.length - 37)
                    .toUpperCase()})`}
                  options={
                    <IconButton
                      icon={CloseIcon}
                      onClick={() => setNewUserDialogOpen(false)}
                    />
                  }
                >
                  <Grid container spacing={config.GRID_SPACING}>
                    <Grid item xs={12}>
                      <TextField
                        name={"email"}
                        label={"Email"}
                        onBlur={formik.handleBlur}
                        onChange={formik.handleChange}
                        value={formik.values.email}
                        error={
                          formik.touched.email && Boolean(formik.errors.email)
                        }
                        helperText={formik.touched.email && formik.errors.email}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        variant={"filled"}
                        label={"Reason to create user"}
                        fullWidth
                        onChange={(e) => saveUserLogMessage(e.target.value)}
                        value={userLogMessage}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Stack direction={"row"} gap={1} flexWrap={"wrap"}>
                        <LabelledCheckbox
                          label={"Enabled"}
                          name={"enabled"}
                          checked={formik.values.enabled}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                        />
                        <LabelledCheckbox
                          label={"Send Email"}
                          name={"sendEmail"}
                          checked={formik.values.sendEmail}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                        />
                        <LabelledCheckbox
                          label={"Set Password"}
                          name={"setPassword"}
                          checked={formik.values.setPassword}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                        />
                      </Stack>
                    </Grid>
                    {formik.values.setPassword && (
                      <Grid item xs={12}>
                        <PasswordField
                          onChange={(password) =>
                            formik.setFieldValue("password", password)
                          }
                        />
                      </Grid>
                    )}
                    <Grid item xs={12}>
                      <b>{"Permissions"}</b>
                      <Stack
                        container
                        direction={"row"}
                        gap={1}
                        flexWrap={"wrap"}
                      >
                        {formikPerms.map((perm, index) => (
                          <LabelledCheckbox
                            key={index}
                            label={readableName(formikPermToPerm[perm])}
                            name={perm}
                            checked={formik.values[perm]}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                          />
                        ))}
                      </Stack>
                    </Grid>
                    <Grid item xs={12}>
                      <LabelledCheckbox
                        label={"Expire permissions"}
                        name={"timeoutRequired"}
                        checked={formik.values.timeoutRequired}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Stack
                        direction={"row"}
                        alignItems={"center"}
                        gap={1}
                        flexWrap={"wrap"}
                      >
                        <Grid item md={4}>
                          <TextField
                            name={"timeoutDate"}
                            label={"Expiry Date/Time"}
                            placeholder={"ISO date with optional hour:minute"}
                            onBlur={formik.handleBlur}
                            onChange={formik.handleChange}
                            value={
                              formik.values.timeoutRequired
                                ? formik.values.timeoutDate
                                : ""
                            }
                            disabled={!formik.values.timeoutRequired}
                            error={
                              formik.touched.timeoutDate &&
                              Boolean(formik.errors.timeoutDate)
                            }
                          />
                        </Grid>
                        <LabelledCheckbox
                          label={"All"}
                          name={"expireAll"}
                          checked={formik.values.expireAll}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          disabled={!formik.values.timeoutRequired}
                        />
                        {formikPerms.map((perm, index) => (
                          <LabelledCheckbox
                            key={index}
                            label={readableName(formikPermToPerm[perm])}
                            name={"expire_" + perm}
                            checked={
                              formik.values.expireAll
                                ? formik.values[perm]
                                : formik.values["expire_" + perm]
                            }
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            disabled={
                              !formik.values.timeoutRequired ||
                              formik.values.expireAll
                            }
                          />
                        ))}
                      </Stack>
                    </Grid>
                    <Grid
                      item
                      container
                      xs={12}
                      justifyContent={"flex-end"}
                      alignItems={"center"}
                    >
                      <Grid item>
                        <Button
                          onClick={formik.handleSubmit}
                          isLoading={isLoading}
                          isDisabled={isLoading || !hasUserLogMessage}
                          hasError={isError}
                          errorTooltip={"Failed to create user."}
                        >
                          {"Create User"}
                        </Button>
                      </Grid>
                    </Grid>
                  </Grid>
                </Component>
              )}
            </Formik>
          </Dialog>
        )}
        {responseData && (
          <Component title={"Server Response"}>
            <Grid item xs={12}>
              <Typography>{"Response:"}</Typography>
            </Grid>
            {newUser && (
              <Grid item xs={12}>
                <User
                  permissionsList={permList}
                  user={newUser}
                  disabled={true}
                />
              </Grid>
            )}
            <Grid item xs={12}>
              <MDEditor.Markdown source={responseData} />
            </Grid>
            <Grid item xs={12}>
              {" "}
            </Grid>
          </Component>
        )}
      </DashboardContent>
    </AdminDashboard>
  );
};

const mapStateToProps = (state) => {
  return {
    userLogMessage: adminSelectors.userLogMessage(state),
    hasUserLogMessage: adminSelectors.hasUserLogMessage(state),
  };
};

const mapDispatchToProps = {
  saveUserLogMessage: adminActions.userLogMessage.update,
};

export default connect(mapStateToProps, mapDispatchToProps)(NewUser);
